import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import {
  Instagram,
  Mail,
  ArrowUpRight,
  Sparkles,
  Leaf,
  ShieldCheck,
  Flame,
  HeartHandshake,
} from "lucide-react";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------- Data -------------- */
const INGREDIENTS = [
  {
    name: "Aloe Vera Gel",
    short: "Hydrate · Soothe",
    blurb:
      "Hydrates and soothes the scalp while helping to reduce dryness and frizz after heat exposure.",
    img: "https://images.unsplash.com/photo-1570295835271-04c05b4ed943?auto=format&fit=crop&w=900&q=80",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    name: "Argan Oil",
    short: "Nourish · Soften",
    blurb:
      "Rich in antioxidants and essential fatty acids to nourish, soften, and protect strands from heat.",
    img: "https://images.unsplash.com/photo-1515377905703-c4788e51af15?auto=format&fit=crop&w=900&q=80",
    span: "md:col-span-2",
  },
  {
    name: "Jojoba Oil",
    short: "Balance · Moisturize",
    blurb:
      "Mimics the scalp's natural oils to moisturize, balance, and support a resilient hair foundation.",
    img: "https://images.unsplash.com/photo-1608571702600-5a5419d31475?auto=format&fit=crop&w=900&q=80",
    span: "md:col-span-2",
  },
  {
    name: "Panthenol (B5)",
    short: "Strengthen · Elasticity",
    blurb:
      "Helps strengthen hair, improve elasticity, and lock in moisture for a healthy, lived-in shine.",
    img: "https://images.unsplash.com/photo-1573461160327-b450ce3d8e7f?auto=format&fit=crop&w=900&q=80",
    span: "md:col-span-2",
  },
  {
    name: "Rosemary Extract",
    short: "Scalp · Vitality",
    blurb:
      "Supports a healthy scalp environment and helps promote stronger-looking hair over time.",
    img: "https://images.unsplash.com/photo-1607721098274-e54cbfab475d?auto=format&fit=crop&w=900&q=80",
    span: "md:col-span-2",
  },
];

const PILLARS = [
  { icon: Leaf,         label: "Nourishes & Strengthens" },
  { icon: ShieldCheck,  label: "Heat & Sweat Ready" },
  { icon: Flame,        label: "Made for Movement" },
  { icon: HeartHandshake, label: "Clean & Thoughtful" },
];

const RITUAL = [
  { n: "01", title: "Apply to dry hair", body: "Apply a generous amount throughout dry hair before your workout, sauna, or infrared session — concentrating on the roots and ends." },
  { n: "02", title: "Work it through", body: "Make sure roots and ends are fully coated, then run any remaining product lightly through the mid-lengths." },
  { n: "03", title: "Slick it back", body: "Style as desired. A low bun, slick pony, or braid keeps the mask working." },
  { n: "04", title: "Move, sweat, train", body: "HALO works while you do. Heat activates the botanicals and seals in moisture." },
  { n: "05", title: "Rinse, shampoo, glow", body: "Cleanse as usual after your session. Hair feels softer, stronger, alive." },
];

const FAQS = [
  { q: "What makes HALO different from a regular hair mask?",
    a: "Most masks ask you to sit still. HALO is a pre-workout treatment designed to protect, nourish, and strengthen your hair while you move — engineered for heat, sweat, saunas, and infrared." },
  { q: "Is it safe for color-treated or curly hair?",
    a: "Yes. Our whipped formula is built around clean, organic ingredients (aloe, argan, jojoba, panthenol, rosemary) that play well with color-treated, textured, fine, and curly hair." },
  { q: "How often should I use HALO?",
    a: "As often as you move. Apply before every heated workout, sauna, or infrared session — that's typically 3–6 times per week for our community." },
  { q: "What's coming next?",
    a: "We're building a clean line of supplements for women who move — starting with creatine, pre-workout, and daily essentials. Join the waitlist for first access." },
  { q: "Where do you ship?",
    a: "First drop ships across the U.S. International friends — join the list and we'll let you know when we expand." },
];

/* -------------- Components -------------- */

const Sparkle = ({ className = "" }) => (
  <span className={`halo-spark ${className}`} aria-hidden>
    <Sparkles size={16} strokeWidth={1.4} />
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setTimeout(() => e.target.classList.add("in"), delay);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className={`reveal ${className}`}>
      {children}
    </div>
  );
};

const WaitlistForm = ({ source = "landing_hero", compact = false, testIdPrefix = "hero" }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        name: name.trim() || undefined,
        source,
      });
      setDone(true);
      if (data.already_subscribed) {
        toast.success("You're already on the list ✨");
      } else {
        toast.success("Welcome to the HALO circle ✨");
      }
      setEmail("");
      setName("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done && compact) {
    return (
      <p
        className="font-body text-sm text-[var(--halo-ink)]/70"
        data-testid={`${testIdPrefix}-waitlist-success`}
      >
        <Sparkle /> You&apos;re on the list. We&apos;ll be in touch.
      </p>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={`flex flex-col gap-3 ${compact ? "" : "sm:flex-row sm:items-end"}`}
      data-testid={`${testIdPrefix}-waitlist-form`}
    >
      {!compact && (
        <div className="flex-1">
          <label className="overline block mb-2">Your name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Optional"
            className="halo-input"
            data-testid={`${testIdPrefix}-waitlist-name`}
          />
        </div>
      )}
      <div className="flex-1">
        <label className="overline block mb-2">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@haloformovement.com"
          className="halo-input"
          data-testid={`${testIdPrefix}-waitlist-email`}
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="btn-halo whitespace-nowrap"
        data-testid={`${testIdPrefix}-waitlist-submit`}
      >
        {loading ? "Joining…" : "Join the Waitlist"}
        <ArrowUpRight size={16} strokeWidth={1.6} />
      </button>
    </form>
  );
};

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[var(--halo-cream)]/85 border-b border-[var(--halo-border)]/60"
          : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("top")}
          className="flex items-center gap-2"
          data-testid="nav-logo"
        >
          <Sparkles size={14} className="text-[var(--halo-ink)]" strokeWidth={1.6} />
          <span className="font-display text-2xl tracking-[0.18em] text-[var(--halo-ink)]">HALO</span>
        </button>

        <nav className="hidden md:flex items-center gap-8 text-[0.72rem] uppercase tracking-[0.22em] text-[var(--halo-ink)]/75 font-semibold">
          <button onClick={() => scrollTo("product")} data-testid="nav-product" className="hover:text-[var(--halo-terracotta)] transition-colors">The Mask</button>
          <button onClick={() => scrollTo("ingredients")} data-testid="nav-ingredients" className="hover:text-[var(--halo-terracotta)] transition-colors">Ingredients</button>
          <button onClick={() => scrollTo("ritual")} data-testid="nav-ritual" className="hover:text-[var(--halo-terracotta)] transition-colors">The Ritual</button>
          <button onClick={() => scrollTo("founder")} data-testid="nav-founder" className="hover:text-[var(--halo-terracotta)] transition-colors">Story</button>
          <button onClick={() => scrollTo("faq")} data-testid="nav-faq" className="hover:text-[var(--halo-terracotta)] transition-colors">FAQ</button>
        </nav>

        <button
          onClick={() => scrollTo("waitlist")}
          className="btn-halo !py-2.5 !px-5 !text-[0.7rem]"
          data-testid="nav-join-waitlist"
        >
          Join Waitlist
        </button>
      </div>
    </header>
  );
};

const Marquee = () => {
  const words = [
    "Clean & Thoughtful",
    "Made by a Woman, for Women",
    "Hair Wellness for Movement",
    "Move • Sweat • Protect",
    "Clean & Thoughtful",
    "Made by a Woman, for Women",
    "Hair Wellness for Movement",
    "Move • Sweat • Protect",
  ];
  return (
    <div className="border-y border-[var(--halo-border)] bg-[var(--halo-stone)] overflow-hidden">
      <div className="flex whitespace-nowrap halo-marquee-track py-3">
        {words.map((w, i) => (
          <div key={i} className="flex items-center gap-6 px-6 text-[0.72rem] uppercase tracking-[0.32em] text-[var(--halo-ink)]/70 font-semibold">
            <Sparkles size={12} className="text-[var(--halo-terracotta)]" strokeWidth={1.6} />
            {w}
          </div>
        ))}
      </div>
    </div>
  );
};

const Hero = () => {
  return (
    <section id="top" className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
      <div className="absolute inset-0 halo-grain" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          {/* Left — copy */}
          <div className="lg:col-span-7">
            <Reveal>
              <span className="overline inline-flex items-center gap-2">
                <Sparkles size={12} strokeWidth={1.6} /> The First Drop · Pre-Workout Hair Mask
              </span>
            </Reveal>

            <Reveal delay={80}>
              <h1 className="font-display text-[clamp(3rem,8vw,7rem)] leading-[0.92] tracking-tight mt-6 text-[var(--halo-ink)]">
                Hair wellness
                <br />
                <span className="italic font-normal text-[var(--halo-terracotta)]">for movement.</span>
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="mt-8 max-w-xl text-base md:text-lg text-[var(--halo-ink)]/75 leading-relaxed">
                A hair prep ritual designed to nourish, protect, and strengthen
                your hair before movement. Created for heated Pilates, yoga,
                strength training, saunas, and every workout in between.
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div id="waitlist-inline" className="mt-10 max-w-xl">
                <WaitlistForm source="hero_inline" compact testIdPrefix="hero" />
                <p className="mt-3 text-xs text-[var(--halo-ink)]/55 tracking-wide">
                  Be first in line for the launch · No spam, just rituals.
                </p>
              </div>
            </Reveal>

            <Reveal delay={320}>
              <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-[0.7rem] uppercase tracking-[0.22em] text-[var(--halo-ink)]/70 font-semibold">
                {PILLARS.map(({ icon: Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-2">
                    <Icon size={14} strokeWidth={1.5} className="text-[var(--halo-terracotta)]" />
                    {label}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Right — visual */}
          <div className="lg:col-span-5 relative">
            <Reveal delay={120}>
              <div className="relative">
                <div className="img-zoom halo-portrait aspect-[3/4] rounded-[1.5rem] shadow-[0_30px_80px_-40px_rgba(10,18,42,0.4)]">
                  <img
                    src="https://customer-assets.emergentagent.com/job_active-hair-mask/artifacts/vbh6qjuf_ChatGPT%20Image%20Jun%2025%2C%202026%2C%2005_33_47%20PM.png"
                    alt="HALO Pre-Workout Hair Treatment Mask — slicked-back, treated hair ready for movement"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const ProductShowcase = () => {
  return (
    <section id="product" className="relative py-24 md:py-32 bg-[var(--halo-stone)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 relative">
            <Reveal>
              <div className="aspect-[4/5] flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_active-hair-mask/artifacts/rqhcjjn4_ChatGPT%20Image%20Jun%2025%2C%202026%2C%2001_40_27%20PM.png"
                  alt="HALO matte ceramic jar — Pre-Workout Hair Treatment Mask"
                  className="max-w-full max-h-full object-contain drop-shadow-[0_40px_60px_rgba(10,18,42,0.18)]"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 lg:pl-8">
            <Reveal>
              <span className="overline">Lightweight · Whipped · Powerful</span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-4xl md:text-6xl mt-5 leading-[1.02] tracking-tight">
                A daily ritual that works
                <span className="italic text-[var(--halo-terracotta)]"> while you move.</span>
              </h2>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-7 text-[var(--halo-ink)]/75 text-base md:text-lg leading-relaxed max-w-xl">
                I created HALO after years of watching my hair turn brittle and crisp
                from heated workouts, saunas, and infrared. This whipped pre-workout
                mask is a love letter to women who train hard — designed to shield,
                soften, and strengthen, without weighing you down.
              </p>
            </Reveal>
            <Reveal delay={220}>
              <dl className="mt-10 grid grid-cols-2 gap-6 max-w-md">
                <div>
                  <dt className="overline">Format</dt>
                  <dd className="mt-2 font-display text-2xl">Whipped Mask</dd>
                </div>
                <div>
                  <dt className="overline">Size</dt>
                  <dd className="mt-2 font-display text-2xl">7.5 oz</dd>
                </div>
                <div>
                  <dt className="overline">Made for</dt>
                  <dd className="mt-2 font-display text-2xl">Heat & Sweat</dd>
                </div>
                <div>
                  <dt className="overline">Built with</dt>
                  <dd className="mt-2 font-display text-2xl">5 Botanicals</dd>
                </div>
              </dl>
            </Reveal>
            <Reveal delay={300}>
              <a href="#ingredients" className="btn-ghost-halo mt-10 inline-flex" data-testid="link-explore-ingredients">
                Explore the ingredients <ArrowUpRight size={14} />
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const Ingredients = () => {
  return (
    <section id="ingredients" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="overline">Powered By</span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02] tracking-tight">
                Ingredients that
                <br />
                <span className="italic text-[var(--halo-terracotta)]">care for your hair.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <Reveal delay={140}>
              <p className="text-[var(--halo-ink)]/75 text-base md:text-lg leading-relaxed">
                Five quiet powerhouses, chosen for what they do — not what they
                promise. Clean, organic, and crafted to support your scalp through
                every drop of sweat.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[220px] gap-5 md:gap-6">
          {INGREDIENTS.map((ing, i) => (
            <Reveal
              key={ing.name}
              delay={i * 80}
              className={`group relative ${ing.span} rounded-[1.25rem] overflow-hidden bg-[var(--halo-muted,#F0EBE1)] border border-[var(--halo-border)]`}
            >
              <div className="absolute inset-0">
                <div className="img-zoom h-full">
                  <img src={ing.img} alt={ing.name} />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--halo-ink)]/95 via-[var(--halo-ink)]/55 to-[var(--halo-ink)]/10" />
              </div>
              <div className="relative h-full p-6 flex flex-col justify-end text-[var(--halo-cream)]">
                <span className="text-[0.65rem] uppercase tracking-[0.3em] text-[var(--halo-cream)]/80">
                  {ing.short}
                </span>
                <h3 className="font-display text-2xl md:text-3xl mt-2">{ing.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--halo-cream)]/85 max-w-md">
                  {ing.blurb}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Ritual = () => {
  return (
    <section id="ritual" className="relative py-24 md:py-32 bg-[var(--halo-ink)] text-[var(--halo-cream)] overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none halo-grain" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-6">
            <Reveal>
              <span className="overline" style={{ color: "#E5C9CC" }}>The Ritual</span>
            </Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02] tracking-tight">
                Five steps. One
                <span className="italic" style={{ color: "#E5C9CC" }}> radiant </span>
                ritual.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <Reveal delay={140}>
              <p className="text-[var(--halo-cream)]/75 text-base md:text-lg leading-relaxed">
                A simple, sacred routine that fits between you and your workout.
                Apply before you sweat — and let HALO work while you do.
              </p>
            </Reveal>
          </div>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {RITUAL.map((step, i) => (
            <Reveal key={step.n} delay={i * 90}>
              <li className="h-full border-t border-[var(--halo-cream)]/20 pt-6 flex flex-col gap-3">
                <span className="font-display text-5xl md:text-6xl" style={{ color: "#E5C9CC" }}>
                  {step.n}
                </span>
                <h3 className="font-display text-xl md:text-2xl text-[var(--halo-cream)]">
                  {step.title}
                </h3>
                <p className="text-sm leading-relaxed text-[var(--halo-cream)]/70">
                  {step.body}
                </p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};

const Founder = () => {
  return (
    <section id="founder" className="py-24 md:py-32 bg-[var(--halo-stone)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 relative">
          <Reveal>
            <div className="img-zoom halo-portrait relative aspect-[4/5] rounded-[1.5rem] shadow-2xl" data-testid="founder-daughter">
              <img
                src="https://customer-assets.emergentagent.com/job_active-hair-mask/artifacts/72mt7x39_HALO%20FOR%20MOVEMENT%20FOUNDERS.png"
                alt="Founder with her daughter — co-creators of HALO For Movement"
              />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:pl-8">
          <Reveal>
            <span className="overline">The Founders</span>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-4xl md:text-6xl mt-4 leading-[1.02] tracking-tight">
              Built by a mother
              <br />
              <span className="italic text-[var(--halo-terracotta)]">and her daughter.</span>
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-7 text-[var(--halo-ink)]/75 text-base md:text-lg leading-relaxed max-w-xl">
              I built HALO because I refused to choose between training hard and
              caring for my hair. But I&apos;m not building it alone — my daughter
              is creating this with me, side by side. HALO is our love letter to
              women who move: a clean, thoughtful line of supplements that honors
              strength, self-care, and the next generation we&apos;re raising.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-8 font-script text-4xl text-[var(--halo-terracotta)]" data-testid="founder-signature">
              — with love, the HALO family
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const FutureDrops = () => {
  const drops = [
    { tag: "Coming Next", title: "Creatine", note: "Clean, micronized, for the woman in motion." },
    { tag: "On the Horizon", title: "Pre-Workout", note: "Smooth energy. No jitters. Built for movement." },
    { tag: "Coming Soon", title: "Daily Essentials", note: "A foundational supplement line, designed thoughtfully." },
  ];
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-xl">
            <Reveal><span className="overline">The Line</span></Reveal>
            <Reveal delay={80}>
              <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.05] tracking-tight">
                A clean line of supplements,
                <span className="italic text-[var(--halo-terracotta)]"> in the making.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={160}>
            <p className="text-[var(--halo-ink)]/70 text-base max-w-sm">
              The mask is just the beginning. HALO is becoming a quiet ecosystem of
              clean wellness for women who move.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {drops.map((d, i) => (
            <Reveal key={d.title} delay={i * 90}>
              <div className="relative h-full rounded-[1.25rem] border border-[var(--halo-border)] bg-[var(--halo-cream)] p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(10,18,42,0.4)]">
                <span className="overline">{d.tag}</span>
                <h3 className="font-display text-3xl md:text-4xl mt-4">{d.title}</h3>
                <p className="mt-4 text-[var(--halo-ink)]/70 text-sm leading-relaxed">{d.note}</p>
                <div className="mt-8 flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-[var(--halo-terracotta)] font-semibold">
                  <Sparkles size={12} strokeWidth={1.6} />
                  Join the waitlist
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQ = () => {
  return (
    <section id="faq" className="py-24 md:py-32 bg-[var(--halo-stone)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal><span className="overline">FAQ</span></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.05] tracking-tight">
              Asked &
              <span className="italic text-[var(--halo-terracotta)]"> answered.</span>
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 text-[var(--halo-ink)]/70 text-base leading-relaxed max-w-sm">
              Still curious? Reach out at{" "}
              <a href="mailto:hello@haloformovement.com" className="underline underline-offset-4 hover:text-[var(--halo-terracotta)]" data-testid="link-email-faq">
                hello@haloformovement.com
              </a>
              .
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={120}>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-[var(--halo-border)]"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-display text-lg md:text-2xl py-6 hover:no-underline hover:text-[var(--halo-terracotta)] [&[data-state=open]]:text-[var(--halo-terracotta)]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--halo-ink)]/75 text-base leading-relaxed pb-6 pr-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const WaitlistCTA = () => {
  return (
    <section id="waitlist" className="relative py-24 md:py-32 bg-[var(--halo-ink)] text-[var(--halo-cream)] overflow-hidden">
      <div className="absolute inset-0 halo-grain opacity-30" />
      <div className="max-w-4xl mx-auto px-6 md:px-10 text-center relative">
        <Reveal>
          <span className="overline" style={{ color: "#E5C9CC" }}>
            <Sparkles size={12} strokeWidth={1.6} className="inline -mt-0.5 mr-2" />
            Limited First Drop
          </span>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.98] tracking-tight">
            Be first in line for
            <br />
            <span className="italic" style={{ color: "#E5C9CC" }}>the launch.</span>
          </h2>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-7 text-[var(--halo-cream)]/75 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Join the HALO circle for early access to the pre-workout hair mask —
            plus first looks at the supplements coming next.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <div className="mt-12 max-w-xl mx-auto text-left">
            <div className="bg-[var(--halo-cream)] text-[var(--halo-ink)] rounded-[1.25rem] p-7 md:p-10 shadow-2xl">
              <WaitlistForm source="cta_section" testIdPrefix="cta" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[var(--halo-cream)] border-t border-[var(--halo-border)]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-[var(--halo-ink)]" strokeWidth={1.6} />
            <span className="font-display text-3xl tracking-[0.18em]">HALO</span>
          </div>
          <p className="mt-4 text-sm text-[var(--halo-ink)]/65 max-w-sm leading-relaxed">
            Hair wellness for movement. Clean supplements for women, made by a
            woman. The first drop is almost here.
          </p>
        </div>
        <div className="md:col-span-3">
          <span className="overline">Explore</span>
          <ul className="mt-5 space-y-3 text-sm">
            <li><a href="#product" className="hover:text-[var(--halo-terracotta)] transition-colors" data-testid="footer-link-product">The Mask</a></li>
            <li><a href="#ingredients" className="hover:text-[var(--halo-terracotta)] transition-colors" data-testid="footer-link-ingredients">Ingredients</a></li>
            <li><a href="#ritual" className="hover:text-[var(--halo-terracotta)] transition-colors" data-testid="footer-link-ritual">The Ritual</a></li>
            <li><a href="#founder" className="hover:text-[var(--halo-terracotta)] transition-colors" data-testid="footer-link-founder">Our Story</a></li>
            <li><a href="#faq" className="hover:text-[var(--halo-terracotta)] transition-colors" data-testid="footer-link-faq">FAQ</a></li>
          </ul>
        </div>
        <div className="md:col-span-4">
          <span className="overline">Stay in Touch</span>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <a
                href="https://instagram.com/haloformovement"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 hover:text-[var(--halo-terracotta)] transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram size={16} strokeWidth={1.6} /> @haloformovement
              </a>
            </li>
            <li>
              <a
                href="mailto:hello@haloformovement.com"
                className="inline-flex items-center gap-2 hover:text-[var(--halo-terracotta)] transition-colors"
                data-testid="footer-email"
              >
                <Mail size={16} strokeWidth={1.6} /> hello@haloformovement.com
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[var(--halo-border)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[var(--halo-ink)]/55 tracking-wide">
          <span>© {new Date().getFullYear()} HALO For Movement. All rights reserved.</span>
          <span className="font-script text-base text-[var(--halo-terracotta)]">made with love · for movement</span>
        </div>
      </div>
    </footer>
  );
};

/* -------------- App -------------- */

function App() {
  useEffect(() => {
    // Light health check so we know the API is reachable
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <div className="App" data-testid="app-root">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "var(--halo-cream)",
            color: "var(--halo-ink)",
            border: "1px solid var(--halo-border)",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
      <Nav />
      <main>
        <Hero />
        <Marquee />
        <ProductShowcase />
        <Ingredients />
        <Ritual />
        <Founder />
        <FutureDrops />
        <FAQ />
        <WaitlistCTA />
      </main>
      <Footer />
    </div>
  );
}

export default App;
